import { gql, useMutation } from '@apollo/client';
import React, { useState } from 'react';
import {
  ListingDetails_ListingData_listing,
  ListingDetails_ListingData_listing_property_media,
} from '../../../../types/graph/ListingDetails_ListingData';

interface IProps {
  listing: ListingDetails_ListingData_listing;
}

const Videos: React.FC<IProps> = ({ listing }) => {
  const [embedValue, setEmbedValue] = useState<string>('');

  const [updateListing] = useMutation(LISTING_VIDEO_UPDATE, {
    onCompleted: () => {
      setEmbedValue('');
    },
  });

  const extractEmbedMeta = (value: string) => {
    let rawValue: string | null | undefined = value;

    if (value.startsWith('<iframe')) {
      let dom = new DOMParser().parseFromString(value, 'text/html').body
        .firstElementChild;
      rawValue = dom?.getAttribute('src');
    }

    // YoutTube Check
    let ytId = null;
    let regex =
      /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    let match = rawValue?.match(regex);
    if (match && match[2].length === 11) {
      ytId = match[2];
    }

    if (ytId) {
      let result = {
        embedProvider: 'youtube',
        embedId: ytId,
        embedUrl: 'https://www.youtube.com/embed/' + ytId,
        embedType: 'video',
      };
      return result;
    }

    // Vimeo Check
    let vId = null;
    regex =
      /^.*(vimeo\.com\/)((channels\/[A-z]+\/)|(groups\/[A-z]+\/videos\/)|(video\/))?([0-9]+)/;
    match = rawValue?.match(regex);
    if (match) {
      vId = match[6];
    }

    if (vId) {
      let result = {
        embedProvider: 'vimeo',
        embedId: vId,
        embedUrl: 'https://player.vimeo.com/video/' + vId,
        embedType: 'video',
      };
      return result;
    }

    return null;
  };

  const handleAddEmbedVideo = () => {
    const embedMeta = extractEmbedMeta(embedValue);
    if (!embedMeta) {
      // Signal error with the embed input to user
    }
    updateListing({
      variables: {
        listingId: listing.id,
        listing: { property: { mediaUpdate: { embed: embedMeta } } },
      },
    });
  };

  const handleDeleteEmbedVideo = (sid: string | null) => {
    if (sid)
      updateListing({
        variables: {
          listingId: listing.id,
          listing: {
            property: {
              mediaUpdate: {
                remove: sid,
              },
            },
          },
        },
      });
    else console.log('Cannot remove, video Id does not exist');
  };

  return (
    <div className="">
      <form action="#" method="POST">
        <div className="grid grid-cols-6 gap-6">
          <div className="col-span-6 sm:col-span-4">
            <label
              htmlFor="price"
              className="block text-sm font-medium text-gray-700"
            >
              Video embed url
            </label>
            <div className="flex space-x-3 items-center">
              <div className="flex-grow">
                <input
                  type="text"
                  name="embedUrl"
                  id="embedUrl"
                  placeholder="Enter a video embed url..."
                  className="mt-1 focus:ring-primary-light focus:border-primary-light block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  value={embedValue}
                  onChange={(e) => setEmbedValue(e.target.value)}
                />
              </div>
              <span className="mt-1">
                <button
                  type="button"
                  className="bg-white inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light"
                  onClick={handleAddEmbedVideo}
                >
                  {/* Heroicon name: solid/plus */}
                  <svg
                    className="-ml-2 mr-1 h-5 w-5 text-gray-400"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>Add</span>
                </button>
              </span>
            </div>
          </div>
        </div>
      </form>

      <div className="mt-7 w-full flex flex-row flex-wrap">
        {listing?.property?.media &&
          listing.property.media
            .filter((i) => !!i && i.type === 'embed' && i.embedType === 'video')
            .map(
              (embed) =>
                embed && (
                  <div
                    key={embed.sid}
                    className="m-1 p-3 bg-gray-100 border border-1 rounded-lg"
                  >
                    {embed.embedProvider === 'youtube' ? (
                      <iframe
                        width="353"
                        height="190"
                        title={embed.sid!}
                        src={`https://www.youtube.com/embed/${embed.embedId}`}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                    ) : embed.embedProvider === 'vimeo' ? (
                      <iframe
                        src={`https://player.vimeo.com/video/${embed.embedId}`}
                        width="353"
                        height="190"
                        title={embed.sid!}
                        frameBorder="0"
                        allow="autoplay; fullscreen; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                    ) : (
                      <iframe
                        src={embed.embedUrl || ''}
                        width="353"
                        height="190"
                        title={embed.sid!}
                      ></iframe>
                    )}
                    <div className="mt-2 flex flex-col justify-items-center ">
                      <button
                        onClick={() => handleDeleteEmbedVideo(embed.sid)}
                        className="bg-white justify-items-center px-2 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 hover:bg-gray-50"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                )
            )}
      </div>
    </div>
  );
};

export default Videos;

const LISTING_VIDEO_UPDATE = gql`
  mutation Listing_VideosUpdateData(
    $listingId: String!
    $listing: ListingInput!
  ) {
    listingUpdate(listingId: $listingId, listing: $listing) {
      id
      property {
        media {
          sid
          filePath
        }
      }
    }
  }
`;
